import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import MonitoringIcon from 'components/material-symbol/icons/Monitoring'
import SectionEmptyState from 'components/section-empty-state'
import { SettingsSection } from '../settings-components'
import useSettingsStyles from '../settings.styles'
import { resultTitle } from './constants'
import useStyles from './styles'

export const percent = (part, whole) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—'

const groupShape = PropTypes.shape({
  count: PropTypes.number.isRequired,
  repliedCount: PropTypes.number,
  bookedCount: PropTypes.number.isRequired,
})

export function Comparison({ label, first, second, minSize, describe }) {
  const classes = useStyles()
  const ready = first.count >= minSize && second.count >= minSize

  return (
    <Box className={classes.resultRow}>
      <Typography className={classes.resultTitle}>{label}</Typography>
      <Typography className={classes.resultDetail}>
        {ready
          ? describe(
            percent(first.bookedCount, first.count),
            percent(second.bookedCount, second.count),
          )
          : `Shows once each group has ${minSize} students (now ${first.count} and ${second.count}).`}
      </Typography>
    </Box>
  )
}

Comparison.propTypes = {
  label: PropTypes.string.isRequired,
  first: groupShape.isRequired,
  second: groupShape.isRequired,
  minSize: PropTypes.number.isRequired,
  describe: PropTypes.func.isRequired,
}

export default function ResultsSection({ results, holdoutPercent }) {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const {
    periodDays,
    outcomeWindowDays,
    minComparisonSize,
    byTrigger,
    automated,
    heldOut,
    personalized,
    asIs,
  } = results
  const sentRows = byTrigger.filter(row => row.count > 0)

  return (
    <SettingsSection
      eyebrow="Results"
      subtitle={`Follow-ups sent in the last ${periodDays} days. A reply or booking counts as a result if it happens within ${outcomeWindowDays} days.`}
      classes={settingsStyles}
    >
      {sentRows.length === 0 && heldOut.count === 0 ? (
        <Box data-testid="results-empty">
          <SectionEmptyState
            Icon={MonitoringIcon}
            title="Results coming soon"
            caption={`Results show up ${outcomeWindowDays} days after your first follow-ups go out.`}
            captionClassName={classes.resultEmptyCaption}
            bordered={false}
          />
        </Box>
      ) : (
        <>
          {sentRows.map(row => (
            <Box
              key={row.trigger}
              className={classes.resultRow}
              data-testid={`result-${row.trigger}`}
            >
              <Typography className={classes.resultTitle}>
                {resultTitle(row.trigger)}
              </Typography>
              <Typography className={classes.resultDetail}>
                {row.count} sent · {percent(row.repliedCount, row.count)}{' '}
                replied · {percent(row.bookedCount, row.count)} booked a lesson
              </Typography>
            </Box>
          ))}
          <Comparison
            label="Automated follow-up vs. none"
            first={automated}
            second={heldOut}
            minSize={minComparisonSize}
            describe={(sent, skipped) =>
              `${sent} of students who got an automated follow-up booked, vs. ${skipped} of students who were skipped.`
            }
          />
          <Comparison
            label="Personalized vs. template as-is"
            first={personalized}
            second={asIs}
            minSize={minComparisonSize}
            describe={(mine, template) =>
              `${mine} of students booked after a message you personalized, vs. ${template} after an unchanged template.`
            }
          />
        </>
      )}
      <Typography className={classes.resultFootnote}>
        To see whether follow-ups work, automations skip about {holdoutPercent}%
        of matching students at random and compare how often each group books.
      </Typography>
    </SettingsSection>
  )
}

ResultsSection.propTypes = {
  results: PropTypes.shape({
    periodDays: PropTypes.number.isRequired,
    outcomeWindowDays: PropTypes.number.isRequired,
    minComparisonSize: PropTypes.number.isRequired,
    byTrigger: PropTypes.arrayOf(
      PropTypes.shape({
        trigger: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        repliedCount: PropTypes.number.isRequired,
        bookedCount: PropTypes.number.isRequired,
      }),
    ).isRequired,
    automated: groupShape.isRequired,
    heldOut: groupShape.isRequired,
    personalized: groupShape.isRequired,
    asIs: groupShape.isRequired,
  }).isRequired,
  holdoutPercent: PropTypes.number.isRequired,
}
